import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './LicensePlates.css'

const LicensePlates = () => {
  const { apiFetch, isOwner, admin } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [plates,         setPlates]         = useState([])
  const [tags,           setTags]           = useState([])
  const [cameras,        setCameras]        = useState([])
  const [editingCamera,  setEditingCamera]  = useState(null) // id or 'new'
  const [cameraForm,     setCameraForm]     = useState({ name: '', camera_ip: '', camera_user: '', camera_password: '' })
  const [syncing,        setSyncing]        = useState(false)
  const [loading,        setLoading]        = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(null)
  const [newPlate,       setNewPlate]       = useState({ plate: '', owner_name: '', tag: '' })
  const [newTag,         setNewTag]         = useState('')
  const [dragOver,       setDragOver]       = useState(false)
  const [editingId,      setEditingId]      = useState(null)
  const [editForm,       setEditForm]       = useState({})

  const fileRef = useRef()

  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
      }).catch(() => {})
    } else if (admin?.tenant_id) {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner, admin?.tenant_id])

  const loadAll = () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    Promise.all([
      apiFetch(`/api/admin/license-plates/${selectedTenant}`).then(r => r.json()),
      apiFetch(`/api/admin/license-plate-tags/${selectedTenant}`).then(r => r.json()),
      apiFetch(`/api/admin/axis-cameras/${selectedTenant}`).then(r => r.json()),
    ])
      .then(([platesData, tagsData, camerasData]) => {
        setPlates(Array.isArray(platesData)  ? platesData  : [])
        setTags(Array.isArray(tagsData)    ? tagsData    : [])
        setCameras(Array.isArray(camerasData) ? camerasData : [])
      })
      .catch(() => setError('Laden fehlgeschlagen'))
      .finally(() => setLoading(false))
  }

  useEffect(loadAll, [selectedTenant])

  const showSuccess = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 3000)
  }

  // ── Tags ──────────────────────────────────────────────────────────────────
  const addTag = async () => {
    const name = newTag.trim()
    if (!name) return
    const res = await apiFetch(`/api/admin/license-plate-tags/${selectedTenant}`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
    if (res.ok) {
      const tag = await res.json()
      setTags(prev => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)))
      setNewTag('')
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Tag konnte nicht erstellt werden')
    }
  }

  const deleteTag = async (id) => {
    const res = await apiFetch(`/api/admin/license-plate-tags/${selectedTenant}/${id}`, { method: 'DELETE' })
    if (res.ok) {
      setTags(prev => prev.filter(t => t.id !== id))
      // Clear tag from plates that used it
      const deleted = tags.find(t => t.id === id)?.name
      if (deleted) setPlates(prev => prev.map(p => p.tag === deleted ? { ...p, tag: '' } : p))
    }
  }

  // ── Plates ────────────────────────────────────────────────────────────────
  const addPlate = async () => {
    const plate = newPlate.plate.trim().toUpperCase()
    if (!plate) return
    setError(null)
    const res = await apiFetch(`/api/admin/license-plates/${selectedTenant}`, {
      method: 'POST',
      body: JSON.stringify({
        plate,
        owner_name: newPlate.owner_name.trim(),
        tag:        newPlate.tag,
      }),
    })
    if (res.ok) {
      const added = await res.json()
      setPlates(prev => [...prev, added].sort((a, b) => a.plate.localeCompare(b.plate)))
      setNewPlate({ plate: '', owner_name: '', tag: '' })
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Hinzufügen fehlgeschlagen')
    }
  }

  const saveEdit = async (id) => {
    const res = await apiFetch(`/api/admin/license-plates/${selectedTenant}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ owner_name: editForm.owner_name, tag: editForm.tag }),
    })
    if (res.ok) {
      const updated = await res.json()
      setPlates(prev => prev.map(p => p.id === id ? updated : p))
      setEditingId(null)
    } else {
      const err = await res.json().catch(() => ({}))
      setError(err.error || 'Speichern fehlgeschlagen')
    }
  }

  const deletePlate = async (id) => {
    const res = await apiFetch(`/api/admin/license-plates/${selectedTenant}/${id}`, { method: 'DELETE' })
    if (res.ok) setPlates(prev => prev.filter(p => p.id !== id))
  }

  const readFileText = (file) =>
    file.text().then(text => {
      // If replacement chars appear, the file is Windows-1252 (common for Excel CSV on Windows)
      if (!text.includes('�')) return text
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload  = e => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(file, 'windows-1252')
      })
    })

  const handleCSV = async (file) => {
    if (!file) return
    const text = await readFileText(file)
    const lines = text.split(/[\r\n]+/).map(l => l.trim()).filter(Boolean)
    const entries = lines
      .filter(l => !/^(plate|kennzeichen|kfz|nummer|license)/i.test(l))
      .map(l => {
        const cols = l.split(/[,;\t]/)
        return {
          plate:      (cols[0] || '').trim().toUpperCase(),
          owner_name: (cols[1] || '').trim(),
          tag:        (cols[2] || '').trim(),
        }
      })
      .filter(e => e.plate.length > 0 && e.plate.length <= 20)

    if (entries.length === 0) {
      setError('Keine gültigen Kennzeichen in der CSV-Datei gefunden.')
      return
    }
    setError(null)
    setSaving(true)

    // Create any tags from the CSV that don't exist yet
    const csvTags       = [...new Set(entries.map(e => e.tag).filter(Boolean))]
    const existingNames = new Set(tags.map(t => t.name))
    const missingTags   = csvTags.filter(name => !existingNames.has(name))

    if (missingTags.length > 0) {
      const created = await Promise.all(
        missingTags.map(name =>
          apiFetch(`/api/admin/license-plate-tags/${selectedTenant}`, {
            method: 'POST',
            body: JSON.stringify({ name }),
          }).then(r => r.ok ? r.json() : null)
        )
      )
      const valid = created.filter(Boolean)
      if (valid.length > 0) {
        setTags(prev => [...prev, ...valid].sort((a, b) => a.name.localeCompare(b.name)))
      }
    }

    const res = await apiFetch(`/api/admin/license-plates/${selectedTenant}/upload`, {
      method: 'POST',
      body: JSON.stringify({ plates: entries }),
    })
    if (res.ok) {
      const result = await res.json()
      setPlates(Array.isArray(result) ? result : [])
      showSuccess(`${result.length} Kennzeichen importiert${missingTags.length > 0 ? `, ${missingTags.length} neue Tags erstellt` : ''}`)
    } else {
      setError('CSV-Import fehlgeschlagen')
    }
    setSaving(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  const downloadTemplate = () => {
    const headers = ['Kennzeichen', 'Name', 'Tag']
    const csv  = '﻿' + headers.join(';') + '\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = 'kennzeichen_vorlage.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const escapeCsv = (v) => {
    const s = String(v ?? '')
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }

  const exportPlates = () => {
    if (plates.length === 0) return
    const headers = ['Kennzeichen', 'Name', 'Tag']
    const rows = plates.map(p => [p.plate, p.owner_name || '', p.tag || ''].map(escapeCsv).join(';'))
    const csv  = '﻿' + [headers.join(';'), ...rows].join('\n') + '\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `kennzeichen_export_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const saveCamera = async () => {
    if (!cameraForm.name.trim()) return
    setSaving(true)
    setError(null)
    const isNew = editingCamera === 'new'
    const url   = isNew
      ? `/api/admin/axis-cameras/${selectedTenant}`
      : `/api/admin/axis-cameras/${selectedTenant}/${editingCamera}`
    const res = await apiFetch(url, {
      method: isNew ? 'POST' : 'PUT',
      body: JSON.stringify(cameraForm),
    })
    if (res.ok) {
      const saved = await res.json()
      setCameras(prev => isNew
        ? [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
        : prev.map(c => c.id === editingCamera ? saved : c)
      )
      setEditingCamera(null)
      setCameraForm({ name: '', camera_ip: '', camera_user: '', camera_password: '' })
      showSuccess(isNew ? 'Kamera hinzugefügt' : 'Kamera gespeichert')
    } else {
      setError('Speichern fehlgeschlagen')
    }
    setSaving(false)
  }

  const deleteCamera = async (id) => {
    if (!window.confirm('Kamera wirklich entfernen?')) return
    const res = await apiFetch(`/api/admin/axis-cameras/${selectedTenant}/${id}`, { method: 'DELETE' })
    if (res.ok) setCameras(prev => prev.filter(c => c.id !== id))
  }

  const syncCamera = async (cameraId) => {
    setSyncing(true)
    await apiFetch(`/api/admin/axis-cameras/${selectedTenant}/${cameraId}/sync`, { method: 'POST' })
    // Reload cameras to get updated sync status
    apiFetch(`/api/admin/axis-cameras/${selectedTenant}`).then(r => r.json())
      .then(data => { if (Array.isArray(data)) setCameras(data) })
      .finally(() => setSyncing(false))
  }

  if (loading) return <div className="loading-state"><span className="spinner" /></div>

  // Camera section — all admins see it, but only owner can add/edit/delete cameras.
  // Tenant admins can sync and change the password.
  const cameraSection = (
    <div className="card">
      <div className="lp-cam-header">
        <div className="section-title">AXIS Kameras</div>
        {isOwner && editingCamera !== 'new' && (
          <button className="btn btn-ghost btn-sm"
            onClick={() => { setEditingCamera('new'); setCameraForm({ name: '', camera_ip: '', camera_user: '', camera_password: '' }) }}>
            + Kamera hinzufügen
          </button>
        )}
      </div>
      <p className="sm-hint">
        Das Passwort wird verschlüsselt (AES-256-GCM) gespeichert und verlässt den Server nie im Klartext.
      </p>

      {/* Full add/edit form — owner only */}
      {isOwner && editingCamera && (
        <div className="lp-cam-form">
          <div className="lp-cam-form-fields">
            <div className="form-group">
              <label>Name</label>
              <input className="form-control" placeholder="z. B. Einfahrt"
                value={cameraForm.name}
                onChange={e => setCameraForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>IP-Adresse</label>
              <input className="form-control" placeholder="192.168.1.100"
                value={cameraForm.camera_ip}
                onChange={e => setCameraForm(f => ({ ...f, camera_ip: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Benutzername</label>
              <input className="form-control" placeholder="root"
                value={cameraForm.camera_user}
                onChange={e => setCameraForm(f => ({ ...f, camera_user: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>
                Passwort
                {editingCamera !== 'new' && cameras.find(c => c.id === editingCamera)?.has_password &&
                  <span className="lp-pw-hint"> (leer lassen um beizubehalten)</span>}
              </label>
              <input className="form-control" type="password"
                placeholder={editingCamera !== 'new' && cameras.find(c => c.id === editingCamera)?.has_password ? '••••••••' : 'Passwort'}
                value={cameraForm.camera_password}
                onChange={e => setCameraForm(f => ({ ...f, camera_password: e.target.value }))} />
            </div>
          </div>
          <div className="lp-axis-footer" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={saveCamera}
              disabled={saving || !cameraForm.name.trim()}>
              {saving ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Speichern'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditingCamera(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {cameras.length === 0 ? (
        <div className="lp-empty">Noch keine Kameras eingetragen</div>
      ) : (
        <table className="lp-cam-table">
          <thead>
            <tr>
              <th>Name</th>
              {isOwner && <th>IP-Adresse</th>}
              {isOwner && <th>Benutzername</th>}
              <th>Passwort</th>
              <th>Zuletzt synchronisiert</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cameras.map(c => (
              <tr key={c.id}>
                <td className="lp-cam-name">
                  {c.sync_pending && <span className="lp-sync-badge" title="Synchronisierung ausstehend">⚠</span>}
                  {c.name}
                </td>
                {isOwner && <td className="lp-meta">{c.camera_ip || '—'}</td>}
                {isOwner && <td className="lp-meta">{c.camera_user || '—'}</td>}
                <td>
                  {editingCamera === c.id && !isOwner ? (
                    <div className="lp-pw-inline">
                      <input className="form-control form-control--sm" type="password"
                        placeholder="Neues Passwort"
                        value={cameraForm.camera_password}
                        onChange={e => setCameraForm(f => ({ ...f, camera_password: e.target.value }))}
                        autoFocus />
                      <button className="btn btn-primary btn-sm"
                        onClick={() => saveCamera()}
                        disabled={saving || !cameraForm.camera_password.trim()}>
                        Speichern
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingCamera(null)}>✕</button>
                    </div>
                  ) : (
                    <span className="lp-meta">{c.has_password ? '✓ gesetzt' : '—'}</span>
                  )}
                </td>
                <td className="lp-date">
                  {c.last_synced_at ? new Date(c.last_synced_at).toLocaleString('de-DE') : '—'}
                </td>
                <td className="lp-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => syncCamera(c.id)} disabled={syncing}>
                    Sync
                  </button>
                  {isOwner ? (
                    <>
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => { setEditingCamera(c.id); setCameraForm({ name: c.name, camera_ip: c.camera_ip, camera_user: c.camera_user, camera_password: '' }) }}>
                        Bearbeiten
                      </button>
                      <button className="btn btn-ghost btn-sm lp-btn-delete" onClick={() => deleteCamera(c.id)}>
                        Löschen
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => { setEditingCamera(c.id); setCameraForm({ name: c.name, camera_ip: c.camera_ip, camera_user: c.camera_user, camera_password: '' }) }}>
                      Passwort ändern
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Kennzeichenverwaltung</h1>
          <p>Whitelist der erlaubten Kennzeichen für die AXIS-Kamera</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {isOwner && tenants.length > 0 && (
        <div className="card lp-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="card">
        <div className="section-title">Tags</div>
        <p className="sm-hint">Tags für die Kategorisierung von Fahrzeugen (z. B. Mitarbeiter, Pool, Besucher).</p>
        <div className="lp-tag-list">
          {tags.map(t => (
            <span key={t.id} className="lp-tag">
              {t.name}
              <button className="lp-tag-del" onClick={() => deleteTag(t.id)} title="Tag löschen">×</button>
            </span>
          ))}
          {tags.length === 0 && <span className="lp-meta">Noch keine Tags</span>}
        </div>
        <div className="lp-tag-add">
          <input
            className="form-control"
            placeholder="Neuer Tag"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTag()}
          />
          <button className="btn btn-ghost" onClick={addTag} disabled={!newTag.trim()}>
            Hinzufügen
          </button>
        </div>
      </div>

      {cameraSection}

      {/* CSV Upload */}
      <div className="card">
        <div className="lp-csv-header">
          <div className="section-title">CSV-Import</div>
          <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
            Vorlage herunterladen
          </button>
        </div>
        <p className="sm-hint">
          Datei hochladen um die <strong>komplette Liste zu ersetzen</strong>.
          Format: <code>Kennzeichen, Name, Tag</code> — eine Zeile pro Fahrzeug.
        </p>
        <div
          className={`lp-drop-zone${dragOver ? ' lp-drop-zone--active' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleCSV(e.dataTransfer.files[0]) }}
          onClick={() => fileRef.current?.click()}
        >
          <span className="lp-drop-icon">📂</span>
          <span>CSV-Datei hierher ziehen oder klicken zum Auswählen</span>
          <input ref={fileRef} type="file" accept=".csv,.txt" hidden
            onChange={e => handleCSV(e.target.files[0])} />
        </div>
      </div>

      {/* Plate List */}
      <div className="card">
        <div className="lp-csv-header">
          <div className="section-title">
            Kennzeichen{plates.length > 0 ? ` (${plates.length})` : ''}
          </div>
          {plates.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={exportPlates}>
              Exportieren
            </button>
          )}
        </div>

        <div className="lp-add-row">
          <input
            className="form-control"
            placeholder="Kennzeichen *"
            value={newPlate.plate}
            onChange={e => setNewPlate(f => ({ ...f, plate: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && addPlate()}
          />
          <input
            className="form-control"
            placeholder="Name (optional)"
            value={newPlate.owner_name}
            onChange={e => setNewPlate(f => ({ ...f, owner_name: e.target.value }))}
          />
          <select
            className="form-control"
            value={newPlate.tag}
            onChange={e => setNewPlate(f => ({ ...f, tag: e.target.value }))}
          >
            <option value="">Tag wählen…</option>
            {tags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          <button className="btn btn-primary" onClick={addPlate} disabled={!newPlate.plate.trim()}>
            Hinzufügen
          </button>
        </div>

        {plates.length === 0 ? (
          <div className="lp-empty">Noch keine Kennzeichen eingetragen</div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Name</th>
                <th>Tag</th>
                <th>Hinzugefügt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plates.map(p => (
                <tr key={p.id}>
                  {editingId === p.id ? (
                    <>
                      <td className="lp-plate">{p.plate}</td>
                      <td>
                        <input className="form-control form-control--sm"
                          value={editForm.owner_name}
                          onChange={e => setEditForm(f => ({ ...f, owner_name: e.target.value }))} />
                      </td>
                      <td>
                        <select className="form-control form-control--sm"
                          value={editForm.tag}
                          onChange={e => setEditForm(f => ({ ...f, tag: e.target.value }))}>
                          <option value="">Kein Tag</option>
                          {tags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="lp-date">{new Date(p.created_at).toLocaleDateString('de-DE')}</td>
                      <td className="lp-actions">
                        <button className="btn btn-primary btn-sm" onClick={() => saveEdit(p.id)}>Speichern</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Abbrechen</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="lp-plate">{p.plate}</td>
                      <td className="lp-meta">{p.owner_name || '—'}</td>
                      <td>
                        {p.tag
                          ? <span className="lp-tag-badge">{p.tag}</span>
                          : <span className="lp-meta">—</span>}
                      </td>
                      <td className="lp-date">{new Date(p.created_at).toLocaleDateString('de-DE')}</td>
                      <td className="lp-actions">
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => { setEditingId(p.id); setEditForm({ owner_name: p.owner_name || '', tag: p.tag || '' }) }}>
                          Bearbeiten
                        </button>
                        <button className="btn btn-ghost btn-sm lp-btn-delete" onClick={() => deletePlate(p.id)}>
                          Löschen
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedTenant && (
        <>
          <VersionHistoryPanel table="license_plates" tenantId={selectedTenant} title="Verlauf – Kennzeichen" onRestored={loadAll} enableTimelineRestore />
          <VersionHistoryPanel table="license_plate_tags" tenantId={selectedTenant} title="Verlauf – Tags" onRestored={loadAll} />
        </>
      )}
    </div>
  )
}

export default LicensePlates
